import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Hotel, MapPin } from 'lucide-react';

/**
 * Affiliate CTA block for 2026 FIFA World Cup travel.
 * Replace placeholder URLs with your affiliate links (Booking.com, Skyscanner, Viator).
 * Optional `city` prop: when set (e.g. from itinerary result), links can target that city for hotels/tours.
 */
const AFFILIATE_LINKS = {
  booking: 'https://www.booking.com/',
  skyscanner: 'https://www.skyscanner.com/',
  viator: 'https://www.viator.com/',
};

function buildBookingUrl(base, city) {
  if (!city) return base;
  const encoded = encodeURIComponent(city);
  if (base.includes('booking.com')) return `https://www.booking.com/searchresults.html?ss=${encoded}`;
  if (base.includes('viator.com')) return `https://www.viator.com/searchResults?text=${encoded}`;
  return base;
}

const AffiliateCTA = ({ className = '', city = '' }) => {
  const bookingHref = buildBookingUrl(AFFILIATE_LINKS.booking, city);
  const viatorHref = buildBookingUrl(AFFILIATE_LINKS.viator, city);
  return (
  <section
    className={`p-6 rounded-xl bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10 border border-amber-200/60 dark:border-amber-700/40 ${className}`}
    aria-label="Book travel for 2026 FIFA World Cup"
  >
    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
      {city ? `Book flights and hotels for ${city}` : 'Book flights and hotels for the 2026 FIFA World Cup'}
    </h3>
    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
      {city ? `Find accommodation and experiences in ${city}.` : 'Plan your trip to the USA, Canada, and Mexico with our partners. Compare flights, accommodation, and tours.'}
    </p>
    <div className="flex flex-wrap gap-3">
      <a
        href={AFFILIATE_LINKS.skyscanner}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 text-sm font-medium hover:border-amber-400 dark:hover:border-amber-500 transition-colors"
      >
        <Plane className="h-4 w-4 text-amber-500" />
        Skyscanner
      </a>
      <a
        href={bookingHref}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 text-sm font-medium hover:border-amber-400 dark:hover:border-amber-500 transition-colors"
      >
        <Hotel className="h-4 w-4 text-amber-500" />
        {city ? `Hotels in ${city}` : 'Booking.com'}
      </a>
      <a
        href={viatorHref}
        target="_blank"
        rel="noopener noreferrer sponsored"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200 text-sm font-medium hover:border-amber-400 dark:hover:border-amber-500 transition-colors"
      >
        <MapPin className="h-4 w-4 text-amber-500" />
        {city ? `Tours in ${city}` : 'Viator'}
      </a>
      <Link
        to="/travel-planner"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors"
      >
        Our travel planner
      </Link>
    </div>
  </section>
  );
};

export default AffiliateCTA;
